import React, { FC, useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, DialogActions, DialogTitle, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material'
import { Dialog } from 'src/components'
import { Report } from 'src/api'
import { getSubreportOptions, isCombinedReport, isReportInProgress } from './regenerateReportHelpers'

export const ALL_SUBREPORTS_VALUE = 'all'

interface RegenerateReportModalProps {
  open: boolean
  report?: Report | null
  isLoading?: boolean
  // Called with the subreport id for a single-subreport regeneration, or with no argument to regenerate the whole report.
  onConfirm: (subreportId?: number) => void
  onClose: () => void
}

export const RegenerateReportModal: FC<RegenerateReportModalProps> = ({ open, report, isLoading = false, onConfirm, onClose }) => {
  const [selectedValue, setSelectedValue] = useState<string>(ALL_SUBREPORTS_VALUE)

  useEffect(() => {
    if (open) setSelectedValue(ALL_SUBREPORTS_VALUE)
  }, [open, report?.id])

  // Call the close handler with no arguments — forwarding the click event into
  // useDialog's closeDialog would end up inside setTimeout and throw (see notFoundSensorsDialog).
  const handleClose = useCallback(() => onClose(), [onClose])

  const handleConfirm = () => {
    onConfirm(selectedValue === ALL_SUBREPORTS_VALUE ? undefined : Number(selectedValue))
  }

  const subreportOptions = getSubreportOptions(report)
  const showSubreportChoice = isCombinedReport(report) && subreportOptions.length > 0
  const reportName = report?.name || report?.type?.displayName || ''

  return (
    <Dialog open={open} onClose={handleClose} title={<DialogTitle>Regenerate report</DialogTitle>} actions={
      <Box mb={2} mx={2}>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={isLoading}>
            Regenerate
            {isLoading ? <CircularProgress size={20} sx={{ marginLeft: '5px' }} /> : null}
          </Button>
        </DialogActions>
      </Box>
    }>
      <Box maxWidth="450px">
        <Typography variant="body1">
          Are you sure you want to regenerate the report "{reportName}"? Generation will re-run in the background and the current PDF file will be replaced once it completes.
        </Typography>
        {isReportInProgress(report) ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This report is already being generated. Regenerating it again will queue one more generation run.
          </Alert>
        ) : null}
        {showSubreportChoice ? (
          <Box mt={2}>
            <Typography variant="subtitle2">What should be regenerated?</Typography>
            <RadioGroup value={selectedValue} onChange={(event) => setSelectedValue(event.target.value)}>
              <FormControlLabel value={ALL_SUBREPORTS_VALUE} control={<Radio />} label="All subreports" />
              {subreportOptions.map((option) => (
                <FormControlLabel key={option.id} value={`${option.id}`} control={<Radio />} label={option.label} />
              ))}
            </RadioGroup>
            <Typography variant="body2" color="text.secondary">
              The combined archive is rebuilt in either case.
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Dialog>
  )
}
